/**
 * iter733 — Extracted from SalesPage.jsx. The largest visual chunk:
 * the daily-grouped sales table (desktop) + mobile card list, with
 * POS-by-branch sub-grouping inside each expanded day, inline edit
 * on non-POS rows, duplicate detection, per-day CSV export, and
 * "Receive credit" / "Delete" actions. All data-testid attributes
 * preserved verbatim so existing E2E tests keep passing.
 *
 * Props:
 *   - sales, branches, customers
 *   - searchFilters
 *   - expandedSalesDates, setExpandedSalesDates
 *   - posBranchExpanded, setPosBranchExpanded
 *   - onExportDayCsv(day)
 *   - onInlineUpdateSale(id, patch)
 *   - onDeleteSale(id)
 *   - onOpenReceiveCredit(sale)
 */
import { format } from 'date-fns';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Trash2, DollarSign, FileDown, AlertTriangle, Copy, Mail } from 'lucide-react';
import { cur } from '@/lib/currency';
import { applySearchFilters } from '@/components/AdvancedSearch';
import { InlineEditCell } from '@/components/common/InlineEditCell';
import { InlineCustomerPicker } from '@/components/common/InlineCustomerPicker';

export function SalesDailyTable({
  sales,
  branches,
  customers,
  searchFilters,
  expandedSalesDates,
  setExpandedSalesDates,
  posBranchExpanded,
  setPosBranchExpanded,
  onExportDayCsv,
  onInlineUpdateSale,
  onDeleteSale,
  onViewJournals = () => {},
  onOpenReceiveCredit,
  invoiceBySale = {},
  pdfSelectedIds = [],
  onTogglePdfSelect = () => {},
  onEmailInvoice = () => {},
  emailingSaleId = null,
}) {
  const filteredSales = applySearchFilters(
    sales.map(s => ({ ...s, payment_mode: s.payment_details?.[0]?.mode || 'cash' })),
    searchFilters,
  );
  const grouped = {};
  filteredSales.forEach(sale => {
    const dateKey = sale.date ? format(new Date(sale.date), 'yyyy-MM-dd') : 'unknown';
    if (!grouped[dateKey]) {
      grouped[dateKey] = { date: sale.date, dateKey, cash: 0, bank: 0, credit: 0, online: 0, total: 0, final: 0, branches: {}, count: 0, items: [] };
    }
    const g = grouped[dateKey];
    g.count++;
    g.items.push(sale);
    (sale.payment_details || []).forEach(p => {
      const amt = p.amount || 0;
      const mode = p.mode || 'cash';
      if (mode === 'cash') g.cash += amt;
      else if (mode === 'bank' || mode === 'card') g.bank += amt;
      else if (mode === 'credit') g.credit += amt;
      else if (mode === 'online' || mode === 'online_platform') g.online += amt;
    });
    const saleTotal = sale.final_amount != null ? sale.final_amount : (sale.amount - (sale.discount || 0));
    g.total += sale.amount || 0;
    g.final += saleTotal;
    const bName = branches.find(b => b.id === sale.branch_id)?.name || 'Other';
    g.branches[bName] = (g.branches[bName] || 0) + saleTotal;
  });
  const dailyData = Object.values(grouped).sort((a, b) => new Date(b.date) - new Date(a.date));

  // iter731 — duplicate detection: POS sales need same system uid +
  // branch; non-POS fall back to amount + customer + branch heuristic.
  dailyData.forEach(day => {
    const dupeKeys = {};
    day.items.forEach(sale => {
      const uid = sale.bill_number || sale.order_number || sale.check_number || sale.external_reference;
      let key;
      if (uid) {
        key = `uid_${sale.branch_id || 'none'}_${uid}`;
      } else {
        const sumPay = (sale.payment_details || []).reduce((s, p) => s + (parseFloat(p.amount) || 0), 0);
        const eff = parseFloat(sale.final_amount) || parseFloat(sale.amount) || sumPay || 0;
        if (eff <= 0 && !sale.customer_id) return;
        key = `${sale.branch_id || 'none'}_${(sale.customer_id || 'walkin')}_${eff.toFixed(2)}`;
      }
      if (!dupeKeys[key]) dupeKeys[key] = [];
      dupeKeys[key].push(sale.id);
    });
    const dupeIds = new Set();
    Object.values(dupeKeys).forEach(ids => {
      if (ids.length > 1) ids.forEach(id => dupeIds.add(id));
    });
    day.duplicateIds = dupeIds;
    day.hasDuplicates = dupeIds.size > 0;
  });

  if (dailyData.length === 0) {
    return <div className="text-center py-8 text-muted-foreground">No sales recorded yet. Add your first sale above!</div>;
  }

  return (
    <div data-testid="sales-daily-table">
      {/* Desktop Table */}
      <div className="border rounded-lg overflow-hidden hidden md:block">
        <div className="bg-muted/50 dark:bg-muted">
          <div className="overflow-x-auto">
            <table className="w-full text-sm table-fixed">
              <thead><tr>
                <th className="px-3 py-3 text-left font-medium text-muted-foreground w-[4%]"></th>
                <th className="px-3 py-3 text-left font-medium text-muted-foreground w-[14%]">Date</th>
                <th className="px-3 py-3 text-right font-medium text-muted-foreground w-[12%]">Total</th>
                <th className="px-3 py-3 text-right font-medium text-muted-foreground w-[12%]">Cash</th>
                <th className="px-3 py-3 text-right font-medium text-muted-foreground w-[12%]">Bank</th>
                <th className="px-3 py-3 text-right font-medium text-muted-foreground w-[12%]">Online</th>
                <th className="px-3 py-3 text-right font-medium text-muted-foreground w-[12%]">Credit</th>
                <th className="px-3 py-3 text-left font-medium text-muted-foreground w-[22%]">Branches</th>
              </tr></thead>
            </table>
          </div>
        </div>
        <div className="max-h-[600px] overflow-y-auto">
          {dailyData.map(day => (
            <div key={day.dateKey}>
              <div className="overflow-x-auto">
                <table className="w-full text-sm table-fixed">
                  <tbody>
                    <tr
                      className={`border-b hover:bg-muted/50 cursor-pointer transition-colors ${day.hasDuplicates ? 'bg-orange-50/60' : ''}`}
                      onClick={() => setExpandedSalesDates(prev => ({ ...prev, [day.dateKey]: !prev[day.dateKey] }))}
                      data-testid={`sales-day-row-${day.dateKey}`}
                    >
                      <td className="px-3 py-3 w-[4%]">
                        {expandedSalesDates[day.dateKey]
                          ? <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-muted-foreground"><polyline points="6 9 12 15 18 9"/></svg>
                          : <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-muted-foreground"><polyline points="9 18 15 12 9 6"/></svg>}
                      </td>
                      <td className="px-3 py-3 w-[14%]">
                        <div className="font-semibold text-sm flex items-center gap-1.5">
                          {day.date ? format(new Date(day.date), 'MMM dd, yyyy') : '-'}
                          {day.hasDuplicates && (
                            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-orange-100 text-orange-700 text-[10px] font-bold border border-orange-300" data-testid={`duplicate-warning-${day.dateKey}`}>
                              <AlertTriangle size={10} /> Duplicate
                            </span>
                          )}
                        </div>
                        <div className="text-[10px] text-muted-foreground">{day.count} entries</div>
                      </td>
                      <td className="px-3 py-3 text-right w-[12%]"><span className="text-sm font-bold text-primary">{cur()} {day.final.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></td>
                      <td className="px-3 py-3 text-right w-[12%]">{day.cash > 0 ? <span className="inline-block px-2 py-1 rounded bg-success/10 text-success text-xs font-semibold border border-success/30">{cur()} {day.cash.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span> : <span className="text-xs text-muted-foreground">-</span>}</td>
                      <td className="px-3 py-3 text-right w-[12%]">{day.bank > 0 ? <span className="inline-block px-2 py-1 rounded bg-info/10 text-info text-xs font-semibold border border-info/30">{cur()} {day.bank.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span> : <span className="text-xs text-muted-foreground">-</span>}</td>
                      <td className="px-3 py-3 text-right w-[12%]">{day.online > 0 ? <span className="inline-block px-2 py-1 rounded bg-purple-50 text-purple-700 text-xs font-semibold border border-purple-200">{cur()} {day.online.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span> : <span className="text-xs text-muted-foreground">-</span>}</td>
                      <td className="px-3 py-3 text-right w-[12%]">{day.credit > 0 ? <span className="inline-block px-2 py-1 rounded bg-warning/10 text-warning text-xs font-semibold border border-warning/30">{cur()} {day.credit.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span> : <span className="text-xs text-muted-foreground">-</span>}</td>
                      <td className="px-3 py-3 w-[22%]">
                        <div className="flex gap-1 flex-wrap items-center">
                          {Object.entries(day.branches || {}).map(([name, amt]) => (
                            <span key={name} className="text-[10px] px-1.5 py-0.5 bg-muted text-foreground rounded border border-border">{name}: {amt.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                          ))}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={(e) => { e.stopPropagation(); onExportDayCsv(day); }}
                            className="h-6 px-1.5 text-[10px] text-info hover:bg-info/10"
                            data-testid={`export-day-csv-${day.dateKey}`}
                            title="Export this day to CSV"
                          >
                            <FileDown size={10} className="mr-0.5" /> CSV
                          </Button>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              {expandedSalesDates[day.dateKey] && (
                <ExpandedDayBlock
                  day={day}
                  branches={branches}
                  customers={customers}
                  posBranchExpanded={posBranchExpanded}
                  setPosBranchExpanded={setPosBranchExpanded}
                  onInlineUpdateSale={onInlineUpdateSale}
                  onDeleteSale={onDeleteSale}
                  onViewJournals={onViewJournals}
                  onOpenReceiveCredit={onOpenReceiveCredit}
                  invoiceBySale={invoiceBySale}
                  pdfSelectedIds={pdfSelectedIds}
                  onTogglePdfSelect={onTogglePdfSelect}
                  onEmailInvoice={onEmailInvoice}
                  emailingSaleId={emailingSaleId}
                />
              )}
            </div>
          ))}
        </div>
        <div className="px-4 py-2.5 text-xs text-muted-foreground bg-muted/50 border-t flex items-center justify-between">
          <span>Showing {filteredSales.length.toLocaleString()} sales across {dailyData.length} days</span>
          <span className="text-[10px] text-muted-foreground">Click a day to expand & manage entries</span>
        </div>
      </div>

      {/* Mobile Cards */}
      <div className="md:hidden space-y-3">
        {dailyData.map(day => (
          <div key={day.dateKey} className="border rounded-xl overflow-hidden dark:border-stone-700" data-testid={`sales-mobile-card-${day.dateKey}`}>
            <div
              className={`p-3 cursor-pointer active:bg-muted/50 ${day.hasDuplicates ? 'bg-orange-50/60' : ''}`}
              onClick={() => setExpandedSalesDates(prev => ({ ...prev, [day.dateKey]: !prev[day.dateKey] }))}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-sm font-outfit dark:text-white">{day.date ? format(new Date(day.date), 'MMM dd, yyyy') : '-'}</span>
                  {day.hasDuplicates && <AlertTriangle size={12} className="text-orange-500" />}
                </div>
                <span className="font-bold text-sm text-primary">{cur()} {day.final.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {day.cash > 0 && <span className="px-2 py-0.5 rounded-full bg-success/10 text-success text-[10px] font-semibold border border-success/30">Cash {day.cash.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>}
                {day.bank > 0 && <span className="px-2 py-0.5 rounded-full bg-info/10 text-info text-[10px] font-semibold border border-info/30">Bank {day.bank.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>}
                {day.online > 0 && <span className="px-2 py-0.5 rounded-full bg-purple-50 text-purple-700 text-[10px] font-semibold border border-purple-200">Online {day.online.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>}
                {day.credit > 0 && <span className="px-2 py-0.5 rounded-full bg-warning/10 text-warning text-[10px] font-semibold border border-warning/30">Credit {day.credit.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>}
              </div>
              <div className="flex items-center justify-between mt-1.5">
                <span className="text-[10px] text-muted-foreground">{day.count} entries</span>
                <div className="flex gap-1 flex-wrap">
                  {Object.entries(day.branches || {}).map(([name]) => (
                    <span key={name} className="text-[10px] px-1.5 py-0.5 bg-muted text-muted-foreground rounded">{name}</span>
                  ))}
                </div>
              </div>
            </div>
            {expandedSalesDates[day.dateKey] && (
              <div className="border-t bg-muted/50 dark:bg-muted/50 divide-y divide-border dark:divide-stone-700">
                {day.items.map(sale => {
                  const remaining = (sale.credit_amount || 0) - (sale.credit_received || 0);
                  return (
                    <div key={sale.id} className="p-3" data-testid={`sale-mobile-detail-${sale.id}`}>
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-1.5">
                          <Badge variant="secondary" className="capitalize text-[10px]">{sale.sale_type}</Badge>
                          <span className="text-xs text-muted-foreground">{branches.find(b => b.id === sale.branch_id)?.name}</span>
                        </div>
                        <span className="font-bold text-sm">{cur()} {(sale.final_amount || sale.amount || 0).toFixed(2)}</span>
                      </div>
                      <div className="flex flex-wrap gap-1 mb-1.5">
                        {(sale.payment_details || []).map((p, i) => (
                          <span
                            key={i}
                            className={`px-1.5 py-0.5 rounded text-[10px] font-medium border ${p.mode === 'cash' ? 'bg-success/10 text-success border-success/30' : p.mode === 'bank' ? 'bg-info/10 text-info border-info/30' : p.mode === 'online_platform' ? 'bg-purple-50 text-purple-700 border-purple-200' : 'bg-warning/10 text-warning border-warning/30'}`}
                          >{p.mode}: {p.amount?.toFixed(0)}</span>
                        ))}
                      </div>
                      <div className="flex items-center gap-2 justify-end">
                        {remaining > 0 && <Button size="sm" variant="outline" onClick={() => onOpenReceiveCredit(sale)} className="h-7 px-2 text-xs">Receive</Button>}
                        <Button size="sm" variant="ghost" onClick={() => onViewJournals(sale.id)} className="h-7 px-2 text-xs text-info" data-testid={`sale-journals-m-${sale.id}`}>JE</Button>
                        <Button size="sm" variant="outline" onClick={() => onDeleteSale(sale.id)} className="h-7 px-2 text-xs text-error"><Trash2 size={12} /></Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ))}
        <div className="text-xs text-muted-foreground text-center py-2">Showing {filteredSales.length.toLocaleString()} sales across {dailyData.length} days</div>
      </div>
    </div>
  );
}

/**
 * iter733 — Inner block that renders the expanded contents of a day
 * row: POS sales grouped by branch (each branch collapsible into its
 * raw bills) plus the legacy individually-rendered non-POS rows
 * (manual entries / credit notes) with inline edit support.
 */
function ExpandedDayBlock({
  day,
  branches,
  customers,
  posBranchExpanded,
  setPosBranchExpanded,
  onInlineUpdateSale,
  onDeleteSale,
  onViewJournals = () => {},
  onOpenReceiveCredit,
  invoiceBySale = {},
  pdfSelectedIds = [],
  onTogglePdfSelect = () => {},
  onEmailInvoice = () => {},
  emailingSaleId = null,
}) {
  const posByBranch = {};
  const nonPosItems = [];
  (day.items || []).forEach(sale => {
    if (sale.sale_type === 'pos') {
      const bid = sale.branch_id || 'none';
      if (!posByBranch[bid]) {
        posByBranch[bid] = {
          branch_id: bid,
          branch_name: branches.find(b => b.id === bid)?.name || 'Unassigned',
          count: 0, total: 0, cash: 0, bank: 0, online: 0, credit: 0,
          has_duplicate: false, items: [],
        };
      }
      const g = posByBranch[bid];
      g.count += 1;
      g.total += (sale.final_amount != null ? sale.final_amount : (sale.amount || 0));
      (sale.payment_details || []).forEach(p => {
        const m = p.mode || 'cash';
        const a = p.amount || 0;
        if (m === 'cash') g.cash += a;
        else if (m === 'bank' || m === 'card') g.bank += a;
        else if (m === 'online' || m === 'online_platform') g.online += a;
        else if (m === 'credit') g.credit += a;
      });
      if (day.duplicateIds.has(sale.id)) g.has_duplicate = true;
      g.items.push(sale);
    } else {
      nonPosItems.push(sale);
    }
  });
  const posGroups = Object.values(posByBranch).sort((a, b) => b.total - a.total);

  return (
    <div className="bg-muted/50 border-b">
      {posGroups.map(g => {
        const groupKey = `${day.dateKey}|${g.branch_id}`;
        const isOpen = !!posBranchExpanded[groupKey];
        return (
          <div key={groupKey} data-testid={`pos-branch-group-${groupKey}`}>
            <div
              className={`flex items-center gap-2 px-3 py-2.5 pl-10 border-b cursor-pointer hover:bg-white/60 ${g.has_duplicate ? 'bg-orange-50/60' : 'bg-emerald-50/40'}`}
              onClick={() => setPosBranchExpanded(p => ({ ...p, [groupKey]: !p[groupKey] }))}
            >
              <span className="text-muted-foreground">
                {isOpen
                  ? <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 12 15 18 9"/></svg>
                  : <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>}
              </span>
              <Badge className="bg-emerald-100 text-emerald-700 text-[10px] border-0">POS</Badge>
              <span className="font-semibold text-sm" data-testid={`pos-branch-name-${groupKey}`}>{g.branch_name}</span>
              <span className="text-xs text-muted-foreground" data-testid={`pos-branch-count-${groupKey}`}>{g.count} {g.count === 1 ? 'bill' : 'bills'}</span>
              {g.has_duplicate && (
                <Badge className="text-[9px] bg-orange-100 text-orange-700 border-orange-300">
                  <AlertTriangle size={9} className="mr-0.5" /> Has duplicate
                </Badge>
              )}
              <span className="ml-auto font-bold text-sm text-primary" data-testid={`pos-branch-total-${groupKey}`}>{cur()} {g.total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              {g.cash > 0 && <span className="text-[10px] text-success font-semibold">cash {g.cash.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>}
              {g.bank > 0 && <span className="text-[10px] text-info font-semibold">bank {g.bank.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>}
              {g.online > 0 && <span className="text-[10px] text-purple-700 font-semibold">online {g.online.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>}
              {g.credit > 0 && <span className="text-[10px] text-warning font-semibold">credit {g.credit.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>}
            </div>
            {isOpen && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <tbody>
                    {g.items.map(sale => {
                      const isDuplicate = day.duplicateIds.has(sale.id);
                      const uid = sale.bill_number || sale.order_number || sale.check_number || sale.external_reference || sale.id.slice(0, 8);
                      return (
                        <tr key={sale.id} className={`border-b border-border hover:bg-white/50 ${isDuplicate ? 'bg-orange-50/80 border-l-4 border-l-orange-400' : ''}`} data-testid={`sale-detail-${sale.id}`}>
                          <td className="px-3 py-1.5 pl-16 w-[14%]">
                            <span className="font-mono text-[11px] text-muted-foreground">#{uid}</span>
                            {isDuplicate && (
                              <span className="ml-1 inline-flex items-center gap-0.5 px-1 py-0.5 rounded bg-orange-100 text-orange-700 text-[9px] font-bold border border-orange-300" data-testid={`duplicate-badge-${sale.id}`}>
                                <Copy size={8} /> Duplicate
                              </span>
                            )}
                            <div className="text-[9px] text-muted-foreground">{sale.date ? format(new Date(sale.date), 'hh:mm a') : ''}</div>
                          </td>
                          <td className="px-3 py-1.5 text-right font-bold text-sm w-[12%]">
                            {cur()} {Number((sale.final_amount != null ? sale.final_amount : sale.amount) || 0).toFixed(2)}
                          </td>
                          <td className="px-3 py-1.5 w-[60%]">
                            <div className="flex gap-1 flex-wrap">
                              {(sale.payment_details || []).map((p, i) => (
                                <span
                                  key={i}
                                  className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-medium border ${p.mode === 'cash' ? 'bg-success/10 text-success border-success/30' : p.mode === 'bank' ? 'bg-info/10 text-info border-info/30' : p.mode === 'online_platform' ? 'bg-purple-50 text-purple-700 border-purple-200' : 'bg-warning/10 text-warning border-warning/30'}`}
                                >{p.mode}: {p.amount?.toFixed(0)}</span>
                              ))}
                            </div>
                          </td>
                          <td className="px-3 py-1.5 text-[11px] text-muted-foreground truncate w-[14%]">{sale.customer_name || sale.notes || ''}</td>
                          <td className="px-3 py-1.5 w-[90px] text-right whitespace-nowrap">
                            <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); onViewJournals(sale.id); }}
                              className="h-7 px-1.5 text-[11px] text-info" data-testid={`sale-journals-pos-${sale.id}`}>JE</Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={(e) => { e.stopPropagation(); onDeleteSale(sale.id); }}
                              className="h-7 px-2 text-error hover:bg-error/10"
                              data-testid={`delete-pos-sale-${sale.id}`}
                              title="Delete this sale"
                            >
                              <Trash2 size={12} />
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                    <tr className="bg-emerald-50/60 border-t-2 border-emerald-300 font-semibold" data-testid={`pos-branch-subtotal-${groupKey}`}>
                      <td className="px-3 py-1.5 pl-16 text-xs text-emerald-900">Subtotal — {g.branch_name} ({g.count})</td>
                      <td className="px-3 py-1.5 text-right text-emerald-900">{cur()} {g.total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      <td colSpan={3}></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}

      {nonPosItems.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <tbody>
              {nonPosItems.map(sale => {
                const remaining = (sale.credit_amount || 0) - (sale.credit_received || 0);
                const isDuplicate = day.duplicateIds.has(sale.id);
                return (
                  <tr key={sale.id} className={`border-b border-border hover:bg-white/50 ${isDuplicate ? 'bg-orange-50/80 border-l-4 border-l-orange-400' : ''}`} data-testid={`sale-detail-${sale.id}`}>
                    <td className="px-3 py-2 pl-10 w-[14%]">
                      <div className="flex items-center gap-1">
                        {invoiceBySale[sale.id] && (
                          <input
                            type="checkbox"
                            className="h-3.5 w-3.5 accent-primary cursor-pointer"
                            checked={pdfSelectedIds.includes(sale.id)}
                            onChange={(e) => { e.stopPropagation(); onTogglePdfSelect(sale.id); }}
                            title={`Select invoice ${invoiceBySale[sale.id].invoice_number} for merged audit PDF`}
                            data-testid={`sale-pdf-select-${sale.id}`}
                          />
                        )}
                        <Badge variant="secondary" className="capitalize text-[10px]">{sale.sale_type}</Badge>
                        {isDuplicate && (
                          <span className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded bg-orange-100 text-orange-700 text-[9px] font-bold border border-orange-300" data-testid={`duplicate-badge-${sale.id}`}>
                            <Copy size={8} /> Possible duplicate
                          </span>
                        )}
                      </div>
                      <div className="text-[9px] text-muted-foreground mt-0.5">{sale.date ? format(new Date(sale.date), 'hh:mm a') : ''}</div>
                    </td>
                    <td className="px-3 py-2 text-sm w-[14%]">{branches.find(b => b.id === sale.branch_id)?.name || '-'}</td>
                    <td className="px-3 py-2 text-right font-bold text-sm w-[12%]" data-testid={`sale-amount-cell-${sale.id}`}>
                      SR{' '}
                      <InlineEditCell
                        value={Number((sale.final_amount != null ? sale.final_amount : sale.amount) || 0)}
                        type="number"
                        step="0.01"
                        onSave={(v) => onInlineUpdateSale(sale.id, { amount: Number(v), final_amount: Number(v) })}
                        format={(v) => Number(v || 0).toFixed(2)}
                        testid={`sale-amount-${sale.id}`}
                        minWidth={70}
                      />
                    </td>
                    <td className="px-3 py-2 w-[22%]">
                      <div className="flex gap-1 flex-wrap">
                        {(sale.payment_details || []).map((p, i) => (
                          <span
                            key={i}
                            className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-medium border ${p.mode === 'cash' ? 'bg-success/10 text-success border-success/30' : p.mode === 'bank' ? 'bg-info/10 text-info border-info/30' : p.mode === 'online_platform' ? 'bg-purple-50 text-purple-700 border-purple-200' : 'bg-warning/10 text-warning border-warning/30'}`}
                          >{p.mode}: {p.amount?.toFixed(0)}</span>
                        ))}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-sm text-muted-foreground truncate w-[10%]" data-testid={`sale-notes-cell-${sale.id}`}>
                      <InlineEditCell
                        value={sale.notes || ''}
                        onSave={(v) => onInlineUpdateSale(sale.id, { notes: v })}
                        format={(v) => v || '-'}
                        testid={`sale-notes-${sale.id}`}
                        minWidth={60}
                      />
                    </td>
                    <td className="px-3 py-2 w-[8%]" data-testid={`sale-customer-cell-${sale.id}`}>
                      <InlineCustomerPicker
                        value={sale.customer_id ? { id: sale.customer_id, name: sale.customer_name || customers.find(c => c.id === sale.customer_id)?.name || '' } : null}
                        customers={customers}
                        onSave={(p) => onInlineUpdateSale(sale.id, p)}
                        testid={`sale-customer-${sale.id}`}
                      />
                      {sale.created_by_name && <div className="text-[9px] text-muted-foreground/70 mt-0.5" data-testid={`sale-created-by-${sale.id}`}>by {sale.created_by_name}</div>}
                    </td>
                    <td className="px-3 py-2 text-right w-[12%]">
                      <div className="flex gap-1 justify-end">
                        {invoiceBySale[sale.id] && (
                          <Button size="sm" variant="outline" disabled={emailingSaleId === sale.id} onClick={(e) => { e.stopPropagation(); onEmailInvoice(sale); }} className="h-7 px-2 text-xs" data-testid={`sale-email-invoice-${sale.id}`} title={`Email invoice ${invoiceBySale[sale.id].invoice_number} PDF to the customer`}>
                            <Mail size={12} className={`mr-0.5 ${emailingSaleId === sale.id ? 'animate-pulse' : ''}`} />{emailingSaleId === sale.id ? '…' : 'Email'}
                          </Button>
                        )}
                        {remaining > 0 && (
                          <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); onOpenReceiveCredit(sale); }} className="h-7 px-2 text-xs" data-testid="receive-credit-button">
                            <DollarSign size={12} className="mr-0.5" />Receive
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); onViewJournals(sale.id); }} className="h-7 px-2 text-xs text-info" data-testid={`sale-journals-${sale.id}`}>JE</Button>
                        <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); onDeleteSale(sale.id); }} className="h-7 px-2 text-error hover:text-error" data-testid="delete-sale-button">
                          <Trash2 size={12} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
